import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PaperPlaneRight, ArrowDown } from "@phosphor-icons/react";
import { TELEGRAM_URL } from "@/App";

const HERO_BG =
  "https://images.unsplash.com/photo-1689443111130-6e9c7dfd8f9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHx0ZWNoJTIwbWluaW1hbGlzdCUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzgzOTU0OTIxfDA&ixlib=rb-4.1.0&q=85";

const lines = ["Expert support", "for social media", "account issues."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.7 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col justify-end overflow-hidden grid-pattern"
    >
      <motion.div
        style={{ y: bgY, opacity: bgOpacity }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover"
          aria-hidden="true"
        />
      </motion.div>
      <div className="absolute inset-0 z-[1] bg-background/85" />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-background via-background/40 to-transparent" />

      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-5 sm:px-8 pb-16 sm:pb-24 pt-32">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-2 w-2 bg-accent animate-pulse" />
          <span className="font-code text-xs uppercase tracking-[0.25em] text-muted-foreground">
            Independent Social Media Support Specialist
          </span>
        </motion.div>

        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.92] text-[3.25rem] sm:text-7xl lg:text-[7.5rem] max-w-6xl">
          {lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className={`block ${i === 2 ? "text-accent" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end gap-8 lg:justify-between">
          <motion.p
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="font-body text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed"
          >
            Disabled accounts, unexpected restrictions, features not working
            the way they should — I help you understand what happened and work
            through the right steps to get things back on track. Clear,
            practical, no runaround.
          </motion.p>

          <motion.div
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="flex items-center gap-4 shrink-0"
          >
            <a
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-telegram-button"
              className="inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-4 font-code text-sm uppercase tracking-[0.15em] font-medium hover:bg-blue-500 transition-colors duration-200 group"
            >
              Get Support
              <PaperPlaneRight
                size={18}
                weight="fill"
                className="group-hover:translate-x-1 transition-transform duration-200"
              />
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-16 flex items-center gap-3 text-muted-foreground"
        >
          <ArrowDown size={16} className="animate-bounce" />
          <span className="font-code text-xs uppercase tracking-[0.2em]">
            Usually replies within a few hours
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
